import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getIdleActivityStorageKey,
  getIdlePageLoadStorageKey,
  readIdleActivityAt,
} from './constants';

const ACTIVITY_PUBLISH_THROTTLE_MS = 1000;
const MAX_TIMER_DELAY_MS = 2_147_483_647;

interface UseIdleLogoutOptions {
  enabled: boolean;
  activityScopeId: string | null;
  countPageLoadAsActivity?: boolean;
  timeoutMs: number;
  warningMs: number | null;
  onLogout: () => void | Promise<void>;
}

interface UseIdleLogoutResult {
  isWarningOpen: boolean;
  isLogoutPending: boolean;
  warningRemainingSeconds: number | null;
  staySignedIn: () => void;
  signOutNow: () => void;
}

function writeStoredTimestamp(storageKey: string, occurredAt: number): void {
  try {
    localStorage.setItem(storageKey, String(occurredAt));
  } catch {
    // In-memory tracking still works when cross-tab storage is unavailable.
  }
}

/**
 * Enforces an absolute inactivity deadline from page loads and observable user input. Once the
 * warning phase begins, local background input cannot reset the deadline. Activity from another
 * tab or an explicit confirmation advances the shared clock and dismisses stale warnings.
 */
export function useIdleLogout({
  enabled,
  activityScopeId,
  countPageLoadAsActivity = true,
  timeoutMs,
  warningMs,
  onLogout,
}: UseIdleLogoutOptions): UseIdleLogoutResult {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isLogoutPending, setIsLogoutPending] = useState(false);
  const [warningRemainingSeconds, setWarningRemainingSeconds] = useState<number | null>(null);
  const onLogoutRef = useRef(onLogout);
  const pageLoadActivityScopeIdRef = useRef<string | null>(null);
  const staySignedInRef = useRef<() => void>(() => undefined);
  const signOutNowRef = useRef<() => void>(() => undefined);
  onLogoutRef.current = onLogout;

  useEffect(() => {
    if (!enabled || !activityScopeId) {
      setIsWarningOpen(false);
      setWarningRemainingSeconds(null);
      staySignedInRef.current = () => undefined;
      signOutNowRef.current = () => undefined;
      return;
    }

    setIsWarningOpen(false);
    setWarningRemainingSeconds(null);
    const storageKey = getIdleActivityStorageKey(activityScopeId);
    const pageLoadStorageKey = getIdlePageLoadStorageKey(activityScopeId);
    const mountedAt = Date.now();
    const storedActivityAt = readIdleActivityAt(activityScopeId, mountedAt);
    if (storedActivityAt === null) {
      // A completed sign-out clears this key. Reset the pending UI only when a fresh authenticated
      // session starts, never during the asynchronous sign-out transition itself.
      setIsLogoutPending(false);
    }
    const isPageLoadForActivityScope = pageLoadActivityScopeIdRef.current !== activityScopeId;
    pageLoadActivityScopeIdRef.current = activityScopeId;
    const pageLoadCanResetDeadline =
      countPageLoadAsActivity &&
      isPageLoadForActivityScope &&
      (storedActivityAt === null || mountedAt - storedActivityAt < timeoutMs);
    let lastActivityAt = pageLoadCanResetDeadline ? mountedAt : (storedActivityAt ?? mountedAt);
    let lastPublishedAt = pageLoadCanResetDeadline ? mountedAt : (storedActivityAt ?? 0);
    let warningOpen = false;
    let logoutStarted = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;

    if (pageLoadCanResetDeadline || storedActivityAt === null) {
      writeStoredTimestamp(storageKey, mountedAt);
      lastPublishedAt = mountedAt;
    }

    const setWarningOpen = (open: boolean, remainingSeconds: number | null = null) => {
      warningOpen = open;
      setIsWarningOpen(open);
      setWarningRemainingSeconds(open ? remainingSeconds : null);
    };

    const clearTimer = () => {
      if (timerId === null) return;
      clearTimeout(timerId);
      timerId = null;
    };

    const triggerLogout = () => {
      if (logoutStarted) return;
      logoutStarted = true;
      clearTimer();
      setWarningOpen(false);
      setIsLogoutPending(true);

      try {
        void Promise.resolve(onLogoutRef.current()).catch(() => undefined);
      } catch {
        // The caller owns its hard-navigation fallback. Keep this hook pending so it cannot
        // restart an expired session or expose an unreachable 0:00 warning.
      }
    };

    function evaluateIdleState() {
      if (logoutStarted) return;
      clearTimer();

      const now = Date.now();
      const elapsed = Math.max(0, now - lastActivityAt);
      if (elapsed >= timeoutMs) {
        triggerLogout();
        return;
      }

      if (warningMs !== null && elapsed >= warningMs) {
        const remainingSeconds = Math.ceil((timeoutMs - elapsed) / 1000);
        if (!warningOpen) {
          setWarningOpen(true, remainingSeconds);
        } else {
          setWarningRemainingSeconds(remainingSeconds);
        }
      }

      const nextBoundary = warningMs !== null && elapsed < warningMs ? warningMs : timeoutMs;
      const remainingMs = Math.max(0, nextBoundary - elapsed);
      const nextDelayMs = warningOpen ? Math.min(remainingMs, 1000) : remainingMs;
      timerId = setTimeout(evaluateIdleState, Math.min(nextDelayMs, MAX_TIMER_DELAY_MS));
    }

    const recordActivity = (forcePublish = false) => {
      if (
        document.visibilityState !== 'visible' ||
        logoutStarted ||
        (warningOpen && !forcePublish)
      ) {
        return;
      }

      const now = Date.now();
      lastActivityAt = now;
      if (forcePublish || now - lastPublishedAt >= ACTIVITY_PUBLISH_THROTTLE_MS) {
        writeStoredTimestamp(storageKey, now);
        lastPublishedAt = now;
      }
    };

    const handleActivity = () => recordActivity();
    const handleActivityStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || event.newValue === null || logoutStarted) return;

      const activityAt = Number(event.newValue);
      const now = Date.now();
      if (!Number.isFinite(activityAt) || activityAt <= lastActivityAt || activityAt > now) return;

      lastActivityAt = activityAt;
      lastPublishedAt = activityAt;
      if (warningOpen) setWarningOpen(false);
      evaluateIdleState();
    };
    const handlePageLoadStorage = (event: StorageEvent) => {
      if (
        event.key !== pageLoadStorageKey ||
        event.newValue === null ||
        logoutStarted
      ) {
        return;
      }

      const pageLoadAt = Number(event.newValue);
      const now = Date.now();
      if (
        !Number.isFinite(pageLoadAt) ||
        pageLoadAt <= lastActivityAt ||
        pageLoadAt > now
      ) {
        return;
      }

      // Loading another same-user Waterlily page is activity, but it must never revive a session
      // whose previously stored deadline has already expired.
      if (now - lastActivityAt >= timeoutMs) {
        evaluateIdleState();
        return;
      }

      lastActivityAt = pageLoadAt;
      lastPublishedAt = pageLoadAt;
      writeStoredTimestamp(storageKey, pageLoadAt);
      if (warningOpen) setWarningOpen(false);
      evaluateIdleState();
    };
    const handleLifecycleCheck = () => evaluateIdleState();
    const handlePageShow = (event: PageTransitionEvent) => {
      evaluateIdleState();
      if (!event.persisted || warningOpen || logoutStarted) return;

      recordActivity(true);
      evaluateIdleState();
    };

    staySignedInRef.current = () => {
      recordActivity(true);
      setWarningOpen(false);
      evaluateIdleState();
    };
    signOutNowRef.current = triggerLogout;

    const activityEvents: (keyof DocumentEventMap)[] = [
      'mousemove',
      'pointerdown',
      'keydown',
      'touchstart',
    ];
    activityEvents.forEach(eventName =>
      document.addEventListener(eventName, handleActivity, { passive: true }),
    );
    document.addEventListener('scroll', handleActivity, { capture: true, passive: true });
    document.addEventListener('visibilitychange', handleLifecycleCheck);
    window.addEventListener('storage', handleActivityStorage);
    window.addEventListener('storage', handlePageLoadStorage);
    window.addEventListener('focus', handleLifecycleCheck);
    window.addEventListener('pageshow', handlePageShow);

    evaluateIdleState();

    return () => {
      clearTimer();
      activityEvents.forEach(eventName => document.removeEventListener(eventName, handleActivity));
      document.removeEventListener('scroll', handleActivity, true);
      document.removeEventListener('visibilitychange', handleLifecycleCheck);
      window.removeEventListener('storage', handleActivityStorage);
      window.removeEventListener('storage', handlePageLoadStorage);
      window.removeEventListener('focus', handleLifecycleCheck);
      window.removeEventListener('pageshow', handlePageShow);
      staySignedInRef.current = () => undefined;
      signOutNowRef.current = () => undefined;
    };
  }, [activityScopeId, countPageLoadAsActivity, enabled, timeoutMs, warningMs]);

  const staySignedIn = useCallback(() => staySignedInRef.current(), []);
  const signOutNow = useCallback(() => signOutNowRef.current(), []);

  return { isWarningOpen, isLogoutPending, warningRemainingSeconds, staySignedIn, signOutNow };
}
